"use client";

import { motion } from 'framer-motion';
import {
  Users,
  Target,
  Lightbulb,
  Award,
  ArrowRight,
  Heart,
  Zap,
  Globe
} from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import dynamic from 'next/dynamic';
import LambLogo from '@/components/lamb-logo';
import { useLanguage } from '@/contexts/language-context';

const AnimatedBackground = dynamic(() => import('@/components/animated-background'), { ssr: false });

const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Heart,
      title: t('about.values.passionDriven.title'),
      description: t('about.values.passionDriven.description'),
      iconColor: "text-red-500",
      bgGradient: "from-red-500/20 to-pink-500/20"
    },
    {
      icon: Lightbulb,
      title: t('about.values.innovationFirst.title'),
      description: t('about.values.innovationFirst.description'),
      iconColor: "text-yellow-500",
      bgGradient: "from-yellow-500/20 to-orange-500/20"
    },
    {
      icon: Users,
      title: t('about.values.clientCentric.title'),
      description: t('about.values.clientCentric.description'),
      iconColor: "text-blue-500",
      bgGradient: "from-blue-500/20 to-cyan-500/20"
    },
    {
      icon: Award,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description'),
      iconColor: "text-purple-500",
      bgGradient: "from-purple-500/20 to-indigo-500/20"
    }
  ];

  const timeline = t('about.timeline');
  const team = t('about.team');

  return (
    <div className="relative min-h-screen">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-deep-purple dark:text-cream mb-6">
                {t('about.title')}
              </h1>
              <p className="text-xl text-deep-purple/80 dark:text-cream/80 mb-8 leading-relaxed">
                {t('about.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-cloud-blue to-cloud-lavender hover:from-cloud-blue/90 hover:to-cloud-lavender/90 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 gradient-button"
                >
                  <Link href="/contact" className="flex items-center gap-2">
                    {t('about.workWithUs')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>

                <Button
                  asChild
                  size="lg"
                  className="secondary-button px-8 py-6 text-lg font-semibold rounded-full"
                  onClick={() => window.open('https://demo.agnuscloud.com', '_blank')}
                >
                  <span>{t('about.ourServices')}</span>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center"
            >
              <LambLogo className="w-80 h-80" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cloud-blue/5 to-cloud-lavender/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-full bg-gradient-to-br from-cloud-blue to-cloud-lavender">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      {t('about.ourMission')}
                    </h2>
                  </div>
                  <p className="text-deep-purple/70 dark:text-cream/70 text-lg leading-relaxed">
                    {t('about.missionText')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-full bg-gradient-to-br from-purple-500 to-pink-500">
                      <Globe className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      {t('about.ourVision')}
                    </h2>
                  </div>
                  <p className="text-deep-purple/70 dark:text-cream/70 text-lg leading-relaxed">
                    {t('about.visionText')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-deep-purple dark:text-cream mb-4">
              {t('about.ourValues')}
            </h2>
            <p className="text-lg text-deep-purple/70 dark:text-cream/70 max-w-2xl mx-auto">
              {t('about.valuesSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <Card className="h-full bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:border-cloud-blue/50 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-6 text-center">
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${value.bgGradient} mb-4`}
                    >
                      <value.icon className={`w-8 h-8 ${value.iconColor}`} />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-deep-purple dark:text-cream mb-3">
                      {value.title}
                    </h3>
                    <p className="text-deep-purple/70 dark:text-cream/70">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-deep-purple/5 to-cloud-blue/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-deep-purple dark:text-cream mb-4">
              {t('about.ourJourney')}
            </h2>
            <p className="text-lg text-deep-purple/70 dark:text-cream/70">
              {t('about.journeySubtitle')}
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cloud-blue to-cloud-lavender" />

            <div className="space-y-12">
              {Array.isArray(timeline) && timeline.map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative flex items-start gap-8"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cloud-blue to-cloud-lavender rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    {item.year}
                  </div>
                  <div className="flex-1 pb-8">
                    <h3 className="text-xl font-semibold text-deep-purple dark:text-cream mb-2">
                      {item.title}
                    </h3>
                    <p className="text-deep-purple/70 dark:text-cream/70">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-deep-purple dark:text-cream mb-4">
              {t('about.meetOurTeam')}
            </h2>
            <p className="text-lg text-deep-purple/70 dark:text-cream/70 max-w-2xl mx-auto">
              {t('about.teamSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Array.isArray(team) && team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:border-cloud-blue/50 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-6 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-cloud-blue to-cloud-lavender rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Users className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-deep-purple dark:text-cream mb-1">
                      {member.name}
                    </h3>
                    <p className="text-cloud-blue font-medium mb-3">
                      {member.role}
                    </p>
                    <p className="text-sm text-deep-purple/70 dark:text-cream/70">
                      {member.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cloud-blue/10 to-cloud-lavender/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-deep-purple dark:text-cream mb-6">
              {t('about.readyToJoin')}
            </h2>
            <p className="text-xl text-deep-purple/70 dark:text-cream/70 mb-8">
              {t('about.readyToJoinSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-cloud-blue to-cloud-lavender hover:from-cloud-blue/90 hover:to-cloud-lavender/90 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 gradient-button"
              >
                <Link href="/contact" className="flex items-center gap-2">
                  {t('about.startAProject')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>

              <Button
                size="lg"
                className="secondary-button px-8 py-6 text-lg font-semibold rounded-full"
                onClick={() => window.open('https://demo.agnuscloud.com', '_blank')}
              >
                {t('about.viewOurWork')}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;